import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Paper,
  Collapse,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  FilterList,
  Clear,
  ExpandMore,
  ExpandLess,
  Tv,
  Monitor,
  Tablet,
  Lightbulb,
  ViewModule,
  Wifi,
  WifiOff,
  Error,
  Build,
} from '@mui/icons-material';

import type { SearchFilters, SortOptions } from '../../types';
import { DeviceType, DeviceStatus } from '../../types';

interface DeviceFiltersProps {
  filters: SearchFilters;
  sortOptions: SortOptions;
  onFiltersChange: (filters: SearchFilters) => void;
  onSortChange: (sort: SortOptions) => void;
  onClearFilters: () => void;
  totalItems?: number;
  onlineItems?: number;
  offlineItems?: number;
  availableLocations?: string[];
}

const DeviceFilters: React.FC<DeviceFiltersProps> = ({
  filters,
  sortOptions,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalItems = 0,
  onlineItems = 0,
  offlineItems = 0,
  availableLocations = [],
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: event.target.value,
    });
  };

  const handleStatusChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      deviceStatuses: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleTypeChange = (event: any) => {
    const value = event.target.value;
    onFiltersChange({
      ...filters,
      deviceTypes: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleLocationChange = (event: any, newValue: string[]) => {
    onFiltersChange({
      ...filters,
      locations: newValue,
    });
  };

  const handleOnlineOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      onlineOnly: event.target.checked,
    });
  };

  const handleOfflineOnlyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      offlineOnly: event.target.checked,
    });
  };

  const handleSortFieldChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      field: event.target.value,
    });
  };

  const handleSortDirectionChange = (event: any) => {
    onSortChange({
      ...sortOptions,
      direction: event.target.value,
    });
  };

  const getActiveFiltersCount = (): number => {
    let count = 0;
    if (filters.search) count++;
    if (filters.deviceStatuses && filters.deviceStatuses.length > 0) count++;
    if (filters.deviceTypes && filters.deviceTypes.length > 0) count++;
    if (filters.locations && filters.locations.length > 0) count++;
    if (filters.onlineOnly) count++;
    if (filters.offlineOnly) count++;
    return count;
  };

  const getStatusIcon = (status: DeviceStatus) => {
    switch (status) {
      case DeviceStatus.ONLINE:
        return <Wifi fontSize="small" />;
      case DeviceStatus.OFFLINE:
        return <WifiOff fontSize="small" />;
      case DeviceStatus.ERROR:
        return <Error fontSize="small" />;
      case DeviceStatus.MAINTENANCE:
        return <Build fontSize="small" />;
      default:
        return <WifiOff fontSize="small" />;
    }
  };

  const getTypeIcon = (type: DeviceType) => {
    switch (type) {
      case DeviceType.TV:
        return <Tv fontSize="small" />;
      case DeviceType.MONITOR:
        return <Monitor fontSize="small" />;
      case DeviceType.TABLET:
        return <Tablet fontSize="small" />;
      case DeviceType.LED_PANEL:
        return <Lightbulb fontSize="small" />;
      case DeviceType.VIDEO_WALL:
        return <ViewModule fontSize="small" />;
      default:
        return <Monitor fontSize="small" />;
    }
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      {/* Основная строка фильтров */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: expanded ? 2 : 0 }}>
        {/* Поиск */}
        <TextField
          placeholder={t('devices.searchPlaceholder')}
          value={filters.search || ''}
          onChange={handleSearchChange}
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: filters.search && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onFiltersChange({ ...filters, search: '' })}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Статус */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            multiple
            value={filters.deviceStatuses || []}
            onChange={handleStatusChange}
            label={t('common.status')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`devices.status.${value.toLowerCase()}`)}
                    size="small"
                    icon={getStatusIcon(value as DeviceStatus)}
                    color={
                      value === DeviceStatus.ONLINE ? 'success' :
                      value === DeviceStatus.OFFLINE ? 'error' :
                      value === DeviceStatus.ERROR ? 'error' : 'warning'
                    }
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(DeviceStatus).map((status) => (
              <MenuItem key={status} value={status}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusIcon(status)}
                  {t(`devices.status.${status.toLowerCase()}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Тип устройства */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('devices.type')}</InputLabel>
          <Select
            multiple
            value={filters.deviceTypes || []}
            onChange={handleTypeChange}
            label={t('devices.type')}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip
                    key={value}
                    label={t(`devices.types.${value.toLowerCase()}`)}
                    size="small"
                    icon={getTypeIcon(value as DeviceType)}
                  />
                ))}
              </Box>
            )}
          >
            {Object.values(DeviceType).map((type) => (
              <MenuItem key={type} value={type}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTypeIcon(type)}
                  {t(`devices.types.${type.toLowerCase()}`)}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Сортировка */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('common.sortBy')}</InputLabel>
          <Select
            value={sortOptions.field || 'name'}
            onChange={handleSortFieldChange}
            label={t('common.sortBy')}
          >
            <MenuItem value="name">{t('devices.name')}</MenuItem>
            <MenuItem value="location">{t('devices.location')}</MenuItem>
            <MenuItem value="status">{t('common.status')}</MenuItem>
            <MenuItem value="last_heartbeat">{t('devices.lastSeen')}</MenuItem>
            <MenuItem value="created_at">{t('common.dateCreated')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>{t('common.order')}</InputLabel>
          <Select
            value={sortOptions.direction || 'asc'}
            onChange={handleSortDirectionChange}
            label={t('common.order')}
          >
            <MenuItem value="asc">{t('common.ascending')}</MenuItem>
            <MenuItem value="desc">{t('common.descending')}</MenuItem>
          </Select>
        </FormControl>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            color={activeFiltersCount > 0 ? 'primary' : 'inherit'}
          >
            {t('common.filters')}
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Button>

          {activeFiltersCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Clear />}
              onClick={onClearFilters}
              color="error"
            >
              {t('common.clear')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Расширенные фильтры */}
      <Collapse in={expanded}>
        <Grid container spacing={2}>
          {/* Местоположение */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Autocomplete
              multiple
              options={availableLocations}
              value={filters.locations || []}
              onChange={handleLocationChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('devices.location')}
                  placeholder={t('devices.selectLocations')}
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Grid>

          {/* Быстрые фильтры */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('devices.quickFilters')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.onlineOnly || false}
                    onChange={handleOnlineOnlyChange}
                    size="small"
                  />
                }
                label={t('devices.onlineOnly')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filters.offlineOnly || false}
                    onChange={handleOfflineOnlyChange}
                    size="small"
                  />
                }
                label={t('devices.offlineOnly')}
              />
            </Box>
          </Grid>

          {/* Статистика */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('devices.statistics')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('devices.totalDevices')}: {totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('devices.onlineDevices')}: {onlineItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('devices.offlineDevices')}: {offlineItems}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Информация о результатах */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            {t('common.showingResults', { total: totalItems })}
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DeviceFilters;



